/* eslint-disable react/no-danger */
import { ComponentsProvider, SCREENS_NAMESPACE } from '@micromag/core/contexts';
import { IntlProvider } from '@micromag/intl';
import '@micromag/intl/locale/fr';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import React, { useCallback, useMemo, useState } from 'react';
import Loadable from 'react-loadable';
import { useLocation } from 'wouter';

import screenComponents from '../screens';

import styles from '../../../styles/blocks/micromag.module.scss';

export const Viewer = Loadable({
    loader: () => import('@micromag/viewer'),
    loading: () => null,
});

const propTypes = {
    screens: PropTypes.arrayOf(
        PropTypes.shape({
            id: PropTypes.string,
        }),
    ),
    story: PropTypes.shape({}),
    isContainer: PropTypes.bool,
    backgroundColor: PropTypes.shape({
        color: PropTypes.string,
        alpha: PropTypes.number,
    }),
    withGradient: PropTypes.bool,
    withoutMenuBar: PropTypes.bool,
    className: PropTypes.string,
    containerClassName: PropTypes.string,
};

const defaultProps = {
    screens: [],
    story: {},
    isContainer: false,
    backgroundColor: null,
    withGradient: false,
    withoutMenuBar: false,
    className: null,
    containerClassName: null,
};

const Micromag = ({
    screens,
    story,
    backgroundColor,
    withGradient,
    withoutMenuBar,
    isContainer,
    className,
    containerClassName,
}) => {
    const [pathname] = useLocation();
    const [screen, setScreen] = useState();
    const onScreenChange = useCallback((newScreen) => {
        const { id = null } = newScreen || {};
        setScreen(id);
    }, []);

    const finalStory = useMemo(
        () => ({ components: screens, ...(story || null) }),
        [screens, story],
    );

    const { color = null, alpha = 0 } = backgroundColor || {};

    const alphaHex = useMemo(
        () => (alpha > 0 ? Math.round(alpha * 255).toString(16) : 'FF'),
        [alpha],
    );
    const background = useMemo(
        () => (color !== null ? `${backgroundColor.color}${alphaHex}` : null),
        [color, alphaHex],
    );

    return (
        <div
            className={classNames({
                [styles.container]: true,
                [styles.withShadow]: withGradient,
                [className]: className !== null,
            })}
            style={{
                ...(background !== null
                    ? {
                          backgroundColor: background,
                      }
                    : {}),
            }}
        >
            <div
                className={classNames([
                    styles.inner,
                    {
                        [styles.isContainer]: isContainer,
                        [containerClassName]: isContainer && containerClassName !== null,
                    },
                ])}
            >
                {withGradient ? <div className={classNames([styles.shadow, styles.left])} /> : null}
                <IntlProvider locale="fr">
                    <ComponentsProvider namespace={SCREENS_NAMESPACE} components={screenComponents}>
                        <Viewer
                            className={styles.viewer}
                            story={finalStory}
                            screen={screen}
                            locale="fr"
                            memoryRouter
                            basePath={pathname}
                            withLandscapeSiblingsScreens
                            landscapeScreenMargin={0}
                            withoutScreensMenu
                            withoutShareMenu
                            withoutMenuShadow
                            withoutMenu={withoutMenuBar}
                            neighborScreensActive={2}
                            neighborScreensMounted={2}
                            onScreenChange={onScreenChange}
                        />
                    </ComponentsProvider>
                </IntlProvider>
                {withGradient ? (
                    <div className={classNames([styles.shadow, styles.right])} />
                ) : null}
            </div>
        </div>
    );
};

Micromag.propTypes = propTypes;
Micromag.defaultProps = defaultProps;

export default Micromag;
